import { FC, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  useGetInvoiceByIdMutation,
  useGetInvoicingLanguages,
} from "../../../../../../api/services/client/clientApis";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
  MuiViewMorePopOver,
} from "../../../../../../components";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
} from "../../../../../../components/statusBadge/StatusBadge";
import {
  EMPTY_VALUE_DASH,
  gridSize,
  viewMorePopOverGridSize,
} from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  formatDate,
  getCurrencyNameAndLocaleById,
  getFormattedAmount,
  getValueOrBlank,
  getValueOrDash,
  isNotEmpty,
} from "../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../redux/slices/appState/appStateSlice";
import { invoiceHistorySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { ViewInvoiceWrapper } from "./ViewInvoiceDetails.style";
import { ViewInvoiceFeesAndExpenseList } from "./ViewInvoiceFeesAndExpenseLists";

interface ViewInvoiceProps {
  invoiceId: string;
  programId: string;
}

export const ViewInvoice: FC<ViewInvoiceProps> = ({ invoiceId, programId }) => {
  const { fetchLabel, fetchValueByKeyOfDropdown, commonLabels } = useSiteCore();

  const { mutate, data: response, isPending } = useGetInvoiceByIdMutation();

  const dispatch = useDispatch();

  const { loggedInUser } = useGetLoggedInUserDetails();

  const {
    data: invoicingLanguages,
    refetchWithParams: refetchInvoicingLanguages,
  } = useGetInvoicingLanguages();

  useEffect(() => {
    if (invoiceId) {
      mutate(invoiceId);
    }

    if (programId) {
      refetchInvoicingLanguages({
        programId: programId,
      });
    }
  }, [invoiceId, programId, mutate]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isPending]);

  const getLabel = (key: keyof typeof invoiceHistorySiteCoreKeys) => {
    return fetchLabel(
      invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      invoiceHistorySiteCoreKeys[key],
    );
  };

  const getCurrencyName = () => {
    return response?.feeCurrency?.name ?? "";
  };

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const currencyList = useSelector(currencyListSelector);

  const getFormattedValue = (value: number | undefined): string => {
    const currencyData = getCurrencyNameAndLocaleById(
      currencyList,
      response?.feeCurrency?.id,
    );

    const formattedAmount = value
      ? `${getCurrencyName()} ${getFormattedAmount(value, currencyData?.currencyLocale)}`
      : EMPTY_VALUE_DASH;
    return formattedAmount;
  };

  return (
    <ViewInvoiceWrapper>
      <MuiBox className="fields-wrapper">
        <MuiTypography variant="h3SemiBold">
          {getLabel("summarySectionHeader")}
        </MuiTypography>
        <MuiGrid container spacing={2}>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryTotalAmount")}
              replaceEmptyWithDash
              value={getFormattedValue(response?.feeOrExpenseAmount)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryTotalTaxAmount")}
              replaceEmptyWithDash
              value={getFormattedValue(response?.totalTaxAmount)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryTotalInvoiceAmount")}
              replaceEmptyWithDash
              value={getFormattedValue(response?.totalInvoiceAmount)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryLocalInvoiceNumber")}
              replaceEmptyWithDash
              value={getValueOrDash(response?.localInvoiceNumber)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={
                response?.statusId === STATUS_BADGE_TYPE.invoiced
                  ? getLabel("summaryInvoiceDate")
                  : getLabel("summaryReversedDate")
              }
              replaceEmptyWithDash
              value={formatDate(response?.date, userCountryDateFormat)}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryPaymentTerms")}
              replaceEmptyWithDash
              value={response?.paymentTerm?.name}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryUtilizedPO")}
              replaceEmptyWithDash
              value={response?.purchaseOrders?.number || "-"}
            />
          </MuiGrid>
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryInvoicedStatus")}
              replaceEmptyWithDash
              value={
                response && (
                  <StatusBadge
                    status={response?.statusId}
                    label={fetchValueByKeyOfDropdown(
                      invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
                      invoiceHistorySiteCoreKeys.invoiceStatusList,
                      response?.statusId,
                    )}
                  />
                )
              }
            />
          </MuiGrid>

          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryInvoiceLanguage")}
              replaceEmptyWithDash
              value={getValueOrBlank(invoicingLanguages?.[0]?.name)}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>

      {response?.reversalReason && (
        <MuiGrid container spacing={2} className="reversal-reason">
          <MuiGrid size={gridSize}>
            <MuiDoubleLabel
              label={getLabel("summaryReversalReason")}
              replaceEmptyWithDash
              value={fetchValueByKeyOfDropdown(
                invoiceHistorySiteCoreKeys.invoiceHistoryTitle,
                invoiceHistorySiteCoreKeys.confirmationPopUpInvoiceReversalReasonList,
                response?.reversalReason ?? "",
              )}
            />
          </MuiGrid>
          {isNotEmpty(response?.reversalComments) && (
            <MuiGrid size={viewMorePopOverGridSize}>
              <MuiDoubleLabel
                label={getLabel("summaryReversalComment")}
                replaceEmptyWithDash
                value={
                  <MuiViewMorePopOver
                    anchorText={commonLabels.viewMore}
                    message={response?.reversalComments}
                    placement="bottom-end"
                  />
                }
              />
            </MuiGrid>
          )}
        </MuiGrid>
      )}
      <MuiBox className="fee-and-expense-label">
        <MuiTypography variant="h3SemiBold">
          {getLabel("feeAndExpensesSectionHeader")}
        </MuiTypography>
      </MuiBox>
      {response?.feesOrExpenses && (
        <ViewInvoiceFeesAndExpenseList
          data={response?.feesOrExpenses}
          getFormattedValue={getFormattedValue}
        />
      )}
    </ViewInvoiceWrapper>
  );
};
